import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { type GeoLocation } from '@smg-real-estate/geo-libs';
import { defineStore } from 'pinia';
import { type ComposerTranslation } from 'vue-i18n';

import { getGeoLocationById, getGeoLocationByUrlName } from '@/api/geoService';
import {
  cityLocale,
  municipalityLocale,
  replaceGeoTagReg,
  zipLocale,
} from '@/helpers/geo/geoUrlHelper';
import { isPlatformIs24 } from '@/helpers/platform';
import {
  CITY_INDICATOR_IN_GEO_ID,
  type UrlParams,
  type UrlParamsWithGeoLocation,
} from '@/stores/types';
import { MapGeoLevel } from '@/types';

export type Locale = string;
export type LocalizedUrls = Array<{ name: string; url: string }>;

interface GeoState {
  searchLocation: GeoLocation | null;
  parentSearchLocation: GeoLocation | null; // only for zipareas
  hasResolveErrors: boolean;
  localizedSEOLinksGroupOne: Record<Locale, LocalizedUrls> | null;
  localizedSEOLinksGroupTwo: Record<Locale, LocalizedUrls> | null;
}

const useGeoStore = defineStore('geoStore', {
  state: (): GeoState => ({
    searchLocation: null,
    parentSearchLocation: null,
    hasResolveErrors: false,
    localizedSEOLinksGroupOne: null,
    localizedSEOLinksGroupTwo: null,
  }),
  actions: {
    async setSearchLocation(location: GeoLocation | null): Promise<void> {
      this.searchLocation = location;
      await this.setParentSearchLocation();
    },
    async resolveSearchLocationByUrl(
      urlName: string,
      language: HgSupportedLanguage,
    ): Promise<void> {
      this.hasResolveErrors = false;
      const location = await getGeoLocationByUrlName(urlName, language);
      if (!location) {
        this.hasResolveErrors = true;
      } else await this.setSearchLocation(location);
    },
    async resolveSearchLocationById(id: string): Promise<void> {
      const location = await getGeoLocationById(id);
      if (!location) {
        this.hasResolveErrors = true;
      }
      await this.setSearchLocation(location);
    },
    async setParentSearchLocation(): Promise<void> {
      if (this.searchLocation?.type === 'ziparea') {
        const parentGeoId = this.searchLocation.parentPaths?.[0].find(
          (item) => item.indexOf(CITY_INDICATOR_IN_GEO_ID) > -1,
        );
        if (parentGeoId) {
          this.parentSearchLocation = await getGeoLocationById(parentGeoId);
        }
      } else {
        this.parentSearchLocation = null;
      }
    },
    setLocalizeSEOLinks(urls: Record<Locale, LocalizedUrls>, group: 'one' | 'two'): void {
      switch (group) {
        case 'one':
          this.localizedSEOLinksGroupOne = urls;
          break;
        case 'two':
          this.localizedSEOLinksGroupTwo = urls;
          break;
        default:
          break;
      }
    },
    resetLocalizedSEOLinks(): void {
      this.localizedSEOLinksGroupOne = null;
      this.localizedSEOLinksGroupTwo = null;
    },
  },
  getters: {
    getUrlByGeoLocation(): UrlParamsWithGeoLocation {
      const isMunicipality = this.searchLocation?.type === 'municipality';

      const generateUrlParams = (
        language: HgSupportedLanguage,
        geoUrlName?: string,
        parentGeoUrlName?: string,
      ): UrlParams => ({
        param: parentGeoUrlName
          ? parentGeoUrlName
          : isMunicipality
            ? geoUrlName!.replace(municipalityLocale[language], cityLocale[language])
            : (geoUrlName ?? ''),
        zipCity: parentGeoUrlName
          ? geoUrlName!.replace(replaceGeoTagReg(zipLocale[language]), '')
          : '',
      });

      return Object.values(HgSupportedLanguage).reduce((acc, language) => {
        const geoUrlName = isPlatformIs24()
          ? this.searchLocation?.is24UrlNames?.[language]
          : this.searchLocation?.urlNames[language];

        const parentGeoUrlName = isPlatformIs24()
          ? this.parentSearchLocation?.is24UrlNames?.[language]
          : this.parentSearchLocation?.urlNames[language];

        acc[language] = generateUrlParams(language, geoUrlName, parentGeoUrlName);
        return acc;
      }, {} as UrlParamsWithGeoLocation);
    },

    getIaziLevelByGeoType(): MapGeoLevel {
      switch (this.searchLocation?.type) {
        case 'canton':
          return MapGeoLevel.Canton;
        case 'municipality':
          return MapGeoLevel.Municipality;
        case 'ziparea':
          return MapGeoLevel.Town;
        default:
          return MapGeoLevel.Country;
      }
    },

    getLocationName(): (t: ComposerTranslation, locale: HgSupportedLanguage) => string {
      return (t: ComposerTranslation, locale: HgSupportedLanguage): string => {
        const defaultDisplay = t('common.country');
        if (!this.searchLocation) {
          return defaultDisplay;
        }

        return this.searchLocation.names[locale][0];
      };
    },

    getLocationSentence(): (
      t: ComposerTranslation,
      locale: HgSupportedLanguage,
      options?: { includePreposition: boolean },
    ) => string {
      return (
        t: ComposerTranslation,
        locale: HgSupportedLanguage,
        { includePreposition = true }: { includePreposition?: boolean } = {},
      ): string => {
        const defaultDisplay = includePreposition ? t('common.inCountry') : t('common.country');
        if (!this.searchLocation) {
          return defaultDisplay;
        }

        const cantonName = this.searchLocation.names[locale][0];
        const prepositionLabel = this.searchLocation.preposition?.[locale];

        switch (this.getIaziLevelByGeoType) {
          case MapGeoLevel.Canton: {
            return [
              includePreposition ? prepositionLabel : '',
              t('common.charts.canton', [cantonName]),
            ]
              .filter(Boolean)
              .join(' ');
          }
          case MapGeoLevel.Municipality:
          case MapGeoLevel.Town: {
            return [includePreposition ? prepositionLabel : '', cantonName]
              .filter(Boolean)
              .join(' ');
          }
          default:
            return defaultDisplay;
        }
      };
    },
  },
});

export default useGeoStore;
